import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Category,
  createCategory,
  deleteCategory,
  fetchCategoryTree,
  updateCategory,
} from "../../api/categories";
import "./CategoryAdmin.css";

type Mode = "add" | "edit";

function CategoryAdmin() {
  const [tree, setTree] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add / edit modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("add");
  const [editTarget, setEditTarget] = useState<Category | null>(null);
  const [name, setName] = useState("");
  const [parentId, setParentId] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<Category | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const navigate = useNavigate();

  const loadTree = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchCategoryTree();
      setTree(data);
    } catch (e: any) {
      if (e.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/login");
        return;
      }
      setError("Failed to load categories. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  const openAdd = (parent: Category | null) => {
    setMode("add");
    setEditTarget(null);
    setName("");
    setParentId(parent ? String(parent.id) : "");
    setModalError(null);
    setModalOpen(true);
  };

  const openEdit = (category: Category) => {
    setMode("edit");
    setEditTarget(category);
    setName(category.name);
    setParentId(category.parentId ? String(category.parentId) : "");
    setModalError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (isSaving) return;
    setModalOpen(false);
    setModalError(null);
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setModalError("Category name is required.");
      return;
    }
    const request = {
      name: trimmed,
      parentId: parentId ? Number(parentId) : null,
    };
    setIsSaving(true);
    setModalError(null);
    try {
      if (mode === "add") {
        await createCategory(request);
      } else if (editTarget) {
        await updateCategory(editTarget.id, request);
      }
      setModalOpen(false);
      await loadTree();
    } catch (e: any) {
      if (e.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/login");
        return;
      }
      if (e.response?.status === 403) {
        setModalError("You don't have permission to manage categories.");
        return;
      }
      setModalError(e?.response?.data?.message || "Failed to save category.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteCategory(deleteTarget.id);
      setDeleteTarget(null);
      await loadTree();
    } catch (e: any) {
      if (e.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/login");
        return;
      }
      if (e.response?.status === 403) {
        setError("You don't have permission to delete categories.");
        setDeleteTarget(null);
        return;
      }
      setError("Failed to delete category. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const renderNode = (category: Category, depth: number) => (
    <div className={`cat-node cat-depth-${depth}`} key={category.id}>
      <div className="cat-row">
        <span className="cat-name">{category.name}</span>
        <div className="cat-actions">
          <button className="btn-small" onClick={() => openAdd(category)}>
            + Sub
          </button>
          <button className="btn-small" onClick={() => openEdit(category)}>
            Edit
          </button>
          <button className="btn-small btn-danger-text" onClick={() => setDeleteTarget(category)}>
            Delete
          </button>
        </div>
      </div>
      {category.children && category.children.length > 0 && (
        <div className="cat-children">
          {category.children.map((child) => renderNode(child, depth + 1))}
        </div>
      )}
    </div>
  );

  return (
    <main className="cat-admin-page">
      <section className="cat-admin-card">
        <div className="cat-admin-header">
          <div>
            <h1>Manage Categories</h1>
            <p>Add, edit, or delete photo categories and subcategories.</p>
          </div>
          <button className="btn primary" onClick={() => openAdd(null)}>
            + Add Top-Level Category
          </button>
        </div>

        {error && <div className="modal-message error cat-error">{error}</div>}

        {isLoading ? (
          <div className="cat-loading">Loading categories...</div>
        ) : tree.length === 0 ? (
          <div className="cat-loading">No categories yet. Add your first one above.</div>
        ) : (
          <div className="cat-tree">{tree.map((node) => renderNode(node, 0))}</div>
        )}
      </section>

      {modalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{mode === "add" ? "Add Category" : "Edit Category"}</h2>
            <p className="modal-subtitle">
              {mode === "add"
                ? "Create a new category. Leave parent empty to make it a top-level category."
                : "Update the name or parent of this category."}
            </p>

            <form
              className="modal-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleSave();
              }}
            >
              <label>
                Name
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Category name"
                  autoFocus
                />
              </label>

              <label>
                Parent category
                <select value={parentId} onChange={(e) => setParentId(e.target.value)}>
                  <option value="">— None (top-level) —</option>
                  {tree.map((node) => (
                    <CategoryOptions key={node.id} node={node} depth={0} excludeId={editTarget?.id} />
                  ))}
                </select>
              </label>

              {modalError && <div className="modal-message error">{modalError}</div>}

              <div className="modal-actions">
                <button type="button" className="btn secondary" onClick={closeModal} disabled={isSaving}>
                  Cancel
                </button>
                <button type="submit" className="btn primary" disabled={isSaving}>
                  {isSaving ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="modal-content modal-content-danger" onClick={(e) => e.stopPropagation()}>
            <h2>Delete Category</h2>
            <p className="modal-subtitle">
              Are you sure you want to delete <strong>"{deleteTarget.name}"</strong>?
              Its subcategories will be promoted to the parent level.
            </p>
            <div className="modal-actions">
              <button type="button" className="btn secondary" onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
                Cancel
              </button>
              <button type="button" className="btn danger" onClick={handleDelete} disabled={isDeleting}>
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

function CategoryOptions({
  node,
  depth,
  excludeId,
}: {
  node: Category;
  depth: number;
  excludeId?: number;
}) {
  if (node.id === excludeId) {
    return null;
  }
  return (
    <>
      <option value={node.id}>
        {"\u00A0".repeat(depth * 3)}
        {depth > 0 ? "└ " : ""}
        {node.name}
      </option>
      {node.children?.map((child) => (
        <CategoryOptions key={child.id} node={child} depth={depth + 1} excludeId={excludeId} />
      ))}
    </>
  );
}

export default CategoryAdmin;
